/**
 * The colours the Reports dashboard's charts draw with, as theme-aware classes.
 *
 * WHY CLASSES AND NOT HEX. The charts used to take literal hues as props, and a
 * literal cannot follow the theme: on the white card six of them measured under
 * the 3:1 a chart mark needs (the pending yellow 1.61, the revenue gold 2.59).
 * Chrome does not resolve `var()` inside an SVG presentation attribute, so a
 * token cannot go in the prop either. It CAN resolve `currentColor`, so each
 * series is drawn in `currentColor` and carries one of these ink classes, which
 * set `color` from a token that theme.css defines per theme. That is the same
 * route the axis ink already takes (CHART_INK in ReportsDashboard.tsx).
 *
 * EVERY CHART HERE NAMES ITS CATEGORIES IN TEXT, on an axis or in a legend, so
 * colour is never the only thing telling two marks apart. That is why a
 * single-series and an ordinal chart get one hue rather than a palette: aging
 * buckets are already named on their axis, and a green-to-red run across them
 * only borrowed the status colours' meanings for something that is not a
 * status.
 *
 * The ratios are recomputed from theme.css by report-chart-colours.test.ts.
 */
import { JOB_STATUS_SOLID, isJobStatus, type JobStatus } from './job-status';

/** One-series charts: revenue, top clients, AR aging, equipment by status. */
export const CHART_SERIES_INK = 'text-fieldiq-accent-signal';

/** The safety tab's incident series, kept red because an incident is a harm. */
export const CHART_INCIDENT_INK = 'text-chart-danger';

/**
 * Job status slices take the app's own status tokens, so a status on this
 * page is the colour it is on the dispatch board, the calendar and the jobs
 * list. Before this, the pie had its own map, and in that map `completed` was
 * green while it is purple everywhere else in the app.
 *
 * Literal class names, never built from the key: Tailwind only emits CSS for
 * strings it can see, and a constructed one would render an uncoloured slice.
 */
export const JOB_STATUS_SLICE_INK: Record<JobStatus, string> = {
  pending: 'text-status-pending',
  scheduled: 'text-status-scheduled',
  active: 'text-status-active',
  on_hold: 'text-status-on-hold',
  completed: 'text-status-completed',
  cancelled: 'text-status-cancelled',
};

/**
 * A status this build has never heard of. The muted ink, not any status's
 * colour, so an unknown slice cannot pass for a real one; the legend beside it
 * echoes the raw value (jobStatusLabel), which is what tells it apart.
 */
export const UNKNOWN_SLICE = { ink: 'text-fieldiq-muted', swatch: 'bg-fieldiq-muted' } as const;

/** Ink for the slice and fill for its legend swatch, for a status off the wire. */
export function jobStatusSlice(status: unknown): { ink: string; swatch: string } {
  if (isJobStatus(status)) return { ink: JOB_STATUS_SLICE_INK[status], swatch: JOB_STATUS_SOLID[status] };
  return UNKNOWN_SLICE;
}
